// Nomad-mon Go! — P4 audio manager, extended for P7 (game shell + narrator VO).
// One WebAudio graph: buffers -> per-shot source -> sfx/music/vo gain -> master
// gain -> destination. Context is created suspended and unlocked on the first
// user gesture (mobile autoplay policy). Master mute lasts the SESSION only.
// Music is a named SLOT SYSTEM: title/battle/pack each map to a file under
// assets/music/ that Jack can replace by dropping a real theme in; a missing
// file falls back to the ambient-loop placeholder. VO is its own channel that
// ducks music, never overlaps itself, and respects the narrator toggle.

import { selectBattleMusic } from './battle-music.js';

const V = 'v188';
// P6 SFX v2: every pack slot has selectable variants (v1 = original ElevenLabs
// suite, a/b = the new candidates). The "a" takes are the shipping defaults;
// audition.html writes per-slot overrides to localStorage and this module
// resolves them at load. `flare` is the epic signature; `legendcrown` is the
// separate legendary fanfare.
export const SFX_VARIANTS = {
  entryhit: { a: 'assets/sfx2/entry-hit.mp3' },
  entrythud: { a: 'assets/sfx2/entry-thud.mp3' },
  // FRONT DOOR 6: the medallion opening's two layers over the entry-hit gong
  // (synth: scripts/gen-entry-spell.mjs). charge = strike/arcs/riser/crackle-boom
  // from the tap; plunge = whoosh/sub-boom/sparkle tail from the dive.
  entrycharge: { a: 'assets/sfx2/entry-charge.mp3' },
  entryplunge: { a: 'assets/sfx2/entry-plunge.mp3' },
  // ENTER GATE: seamless glass-chime + airy-pad bed under the medallion
  // (synth: scripts/gen-entry-ambient.py; main.js loops 0.1-10.1 s of it)
  entryambient: { a: 'assets/sfx2/entry-ambient.mp3' },
  rip:     { v1: 'assets/sfx-src/foil-rip.mp3',     a: 'assets/sfx2/foil-rip-a.mp3',     b: 'assets/sfx2/foil-rip-b.mp3' },
  shimmer: { v1: 'assets/sfx-src/pack-shimmer.mp3', a: 'assets/sfx2/pack-shimmer-a.mp3', b: 'assets/sfx2/pack-shimmer-b.mp3' },
  whoosh:  { v1: 'assets/sfx-src/card-whoosh.mp3',  a: 'assets/sfx2/card-whoosh-a.mp3',  b: 'assets/sfx2/card-whoosh-b.mp3' },
  slam:    { v1: 'assets/sfx-src/card-slam.mp3',    a: 'assets/sfx2/card-slam-a.mp3',    b: 'assets/sfx2/card-slam-b.mp3' },
  sting:   { v1: 'assets/sfx-src/rare-sting.mp3',   a: 'assets/sfx2/rare-sting-a.mp3',   b: 'assets/sfx2/rare-sting-b.mp3' },
  choir:   { v1: 'assets/sfx-src/mythic-choir.mp3', a: 'assets/sfx2/mythic-choir-a.mp3', b: 'assets/sfx2/mythic-choir-b.mp3' },
  tick:    { v1: 'assets/sfx-src/ui-tick.mp3',      a: 'assets/sfx2/ui-tick-a.mp3' },
  flare:   { a: 'assets/sfx2/epic-flare-a.mp3' },
  legendcrown: { a: 'assets/sfx2/legendary-crown.mp3' },
  // Jack's picked card-owned reveal ladder. These are deliberately separate
  // from slam/sting/flare/crown/choir: ceremony layers can ride above the card
  // cue without changing its identity, and audition assets remain replaceable.
  revealcommon:    { a: 'assets/sfx2/reveal-common.mp3' },
  revealrare:      { a: 'assets/sfx2/reveal-rare.mp3' },
  revealepic:      { a: 'assets/sfx2/reveal-epic.mp3' },
  reveallegendary: { a: 'assets/sfx2/reveal-legendary.mp3' },
  revealmythic:    { a: 'assets/sfx2/reveal-mythic.mp3' },
  // SC2-A deck carousel suite
  deckpick:   { a: 'assets/sfx2/deck-pick.mp3' },
  // title10: the title carousel's detent (synth: scripts/gen-carousel-tock.mjs)
  tock:       { a: 'assets/sfx2/carousel-tock.mp3' },
  deckready:  { a: 'assets/sfx2/deck-ready.mp3' },
  deckremove: { a: 'assets/sfx2/deck-remove.mp3' },
  deckstage:  { a: 'assets/sfx2/deck-stage.mp3' },
  // LUCKY DRAW: continuous reel mechanism, one heavy stop transient, and the
  // authored jackpot crown. Kept separate so smoke can prove each stop starts
  // the mechanical clunk on the live WebAudio graph.
  slotspin:    { a: 'assets/sfx2/slot-spin.mp3' },
  slotclunk:   { a: 'assets/sfx2/slot-clunk.mp3' },
  slotjackpot: { a: 'assets/sfx2/slot-jackpot.mp3' },
  // LUCKY DRAW v2 (one reel at a time): the NOMAD SLOTS Reactor set, re-mastered
  // to <= -1 dBTP. ratchet = spinning-reel loop; ratchetslow ends on the clack
  // (1.806 s) that IS the lock; riser/riserfinal climb into the lock; the land
  // masters are the Thor-hammer stacks (final reel = the biggest one).
  slotratchet:     { a: 'assets/sfx2/slot-ratchet.mp3' },
  slotratchetslow: { a: 'assets/sfx2/slot-ratchet-slow.mp3' },
  slotriser:       { a: 'assets/sfx2/slot-riser.mp3' },
  slotriserfinal:  { a: 'assets/sfx2/slot-riser-final.mp3' },
  slotland1:       { a: 'assets/sfx2/slot-land-1.mp3' },
  slotlandfinal:   { a: 'assets/sfx2/slot-land-final.mp3' },
  slotwhoosh:      { a: 'assets/sfx2/slot-whoosh.mp3' },
  // SC8: per-button title-menu press suite + shared hover tick
  msolo:  { a: 'assets/sfx2/menu-solo-a.mp3' },
  mmp:    { a: 'assets/sfx2/menu-mp-a.mp3' },
  mpack:  { a: 'assets/sfx2/menu-pack-a.mp3' },
  mcoll:  { a: 'assets/sfx2/menu-coll-a.mp3' },
  mhover: { a: 'assets/sfx2/menu-hover-a.mp3' },
  // SC12: pack GODLIKE-reveal sustained low rumble (pack context loads SFX_VARIANTS)
  rumble: { a: 'assets/sfx-battle/gastro-rumble.mp3' },
  deep:   { a: 'assets/sfx5/deep.mp3' },
  deepecho: { a: 'assets/sfx5/deep-echo.mp3' },             // v122: deep + KO boom with echo tail                    // v121: Juicy's sub bed under every reveal
  // SC18 GOD PULL: the ceremony's four fixed beds/hits. They live under
  // sfx-battle/ (same staging tree as rumble) but load with the PACK suite,
  // because the god ceremony is a pack-room event.
  dread:   { a: 'assets/sfx-battle/heartbeat-dread.mp3' },   // 8.0s loopable bed
  shatter: { a: 'assets/sfx-battle/reality-shatter.mp3' },   // 4.0s
  eclipse: { a: 'assets/sfx-battle/eclipse-flip.mp3' },      // 6.0s swell
  spaceshock: { a: 'assets/sfx-battle/space-shockwave.mp3' },// 4.0s
  // SC44 DIARRHEA CEREMONY — its own impact layer, deliberately sharing
  // NOTHING with the four god-pull beds above. Jack has now said three
  // separate times that the sound must be distinct and just as big; before
  // this there was no impact layer at all, only the Shao voice pool, which
  // is why it read as "no real sound effects". Wet, organic, eruptive.
  diapress: { a: 'assets/sfx-dia/dia-pressure.mp3' },   // 7.0s build
  diaclench:{ a: 'assets/sfx-dia/dia-clench.mp3' },     // 2.5s silence + drip
  diaerupt: { a: 'assets/sfx-dia/dia-erupt.mp3' },      // 5.0s detonation
  dialens:  { a: 'assets/sfx-dia/dia-lens.mp3' },       // 2.0s lens hits
  diadeluge:{ a: 'assets/sfx-dia/dia-deluge.mp3' },     // 8.0s downpour bed
  diadrain: { a: 'assets/sfx-dia/dia-drain.mp3' },      // 3.5s resolve
  diaflush: { a: 'assets/sfx-dia/dia-flush.mp3' },      // 4.8s literal bowl flush
  diapop:   { a: 'assets/sfx-dia/dia-pop.mp3' },        // 1.35s wet eruptive pops
  // SC44 GOD PULL at 10x. The signature suite. Choral, vast, dry and awed --
  // the exact opposite register to sfx-dia above, on purpose: a player must
  // be able to tell the two ceremonies apart with their eyes shut.
  godascend:    { a: 'assets/sfx-god/god-ascend.mp3' },     // 11.0s riser
  godaaah:      { a: 'assets/sfx-god/god-aaah.mp3' },       // 9.0s THE choral AAAAAHHH
  godsuper:     { a: 'assets/sfx-god/god-supernova.mp3' },  // 6.0s climax hit
  godreliquary: { a: 'assets/sfx-god/god-reliquary.mp3' },  // 7.0s close-up bed
  godcrown:     { a: 'assets/sfx-god/god-choir-crown.mp3' }, // 7.2s additional choir stack
  goddebris:    { a: 'assets/sfx-god/god-debris.mp3' },      // 3.1s dedicated glass field
  // Card growth has its own compact audio signature: three rising strikes and
  // a weighted landing. Milestones add the lower octave and longer crown.
  levelup:        { a: 'assets/sfx-battle/level-up.mp3' },
  levelmilestone: { a: 'assets/sfx-battle/level-milestone.mp3' },
  // Duplicate training: a dry foil break, an electrical pull, then one of
  // three weighted landings so the frequent action does not become a stamp.
  feedcrack:   { a: 'assets/sfx2/feed-crack.mp3' },
  feedsuction: { a: 'assets/sfx2/feed-suction.mp3' },
  feedland1:   { a: 'assets/sfx2/feed-land-a.mp3' },
  feedland2:   { a: 'assets/sfx2/feed-land-b.mp3' },
  feedland3:   { a: 'assets/sfx2/feed-land-c.mp3' },
  // Trading is one authored stereo gesture: opposing paper/foil passes, a
  // centred seal impact, then a warm ownership interval. It is intentionally
  // separate from pack rarity, card growth, god-pull, and duplicate training.
  tradecomplete: { a: 'assets/sfx2/trade-complete.mp3' },
};
export const SFX_DEFAULTS = {
  entryhit: 'a', entrythud: 'a', entrycharge: 'a', entryplunge: 'a', entryambient: 'a', rip: 'a', shimmer: 'a', whoosh: 'a', slam: 'a', sting: 'a', choir: 'a',
  tick: 'a', tock: 'a', flare: 'a', legendcrown: 'a', deckpick: 'a', deckready: 'a', deckremove: 'a', deckstage: 'a',
  slotspin: 'a', slotclunk: 'a', slotjackpot: 'a',
  slotratchet: 'a', slotratchetslow: 'a', slotriser: 'a', slotriserfinal: 'a', slotland1: 'a', slotlandfinal: 'a', slotwhoosh: 'a',
  revealcommon: 'a', revealrare: 'a', revealepic: 'a', reveallegendary: 'a', revealmythic: 'a',
  msolo: 'a', mmp: 'a', mpack: 'a', mcoll: 'a', mhover: 'a', rumble: 'a', deep: 'a', deepecho: 'a',
  dread: 'a', shatter: 'a', eclipse: 'a', spaceshock: 'a',
  diapress: 'a', diaclench: 'a', diaerupt: 'a', dialens: 'a',
  diadeluge: 'a', diadrain: 'a', diaflush: 'a', diapop: 'a',
  godascend: 'a', godaaah: 'a', godsuper: 'a', godreliquary: 'a', godcrown: 'a', goddebris: 'a',
  levelup: 'a', levelmilestone: 'a',
  feedcrack: 'a', feedsuction: 'a', feedland1: 'a', feedland2: 'a', feedland3: 'a',
  tradecomplete: 'a',
};
// P7 battle SFX suite (fixed files, no variants)
export const BATTLE_SFX = {
  supercharge:      'assets/sfx-battle/super-charge.mp3',
  superrelease:     'assets/sfx-battle/super-release.mp3',
  barragecharge:    'assets/sfx-battle/barrage-charge.mp3',
  barragefire:      'assets/sfx-battle/barrage-fire.mp3',
  stormcharge:      'assets/sfx-battle/storm-charge.mp3',
  stormstrike:      'assets/sfx-battle/storm-strike.mp3',
  // SC45 attack silhouettes: each new archetype owns its windup and its landing,
  // so the shape you see and the sound you hear stay attached to each other.
  siegecharge:      'assets/sfx-battle/siege-charge.mp3',
  siegeland:        'assets/sfx-battle/siege-land.mp3',
  slamland:         'assets/sfx-battle/slam-land.mp3',
  quaketravel:      'assets/sfx-battle/quake-travel.mp3',
  quakeland:        'assets/sfx-battle/quake-land.mp3',
  phantomblink:     'assets/sfx-battle/phantom-blink.mp3',
  phantomland:      'assets/sfx-battle/phantom-land.mp3',
  tetherthrow:      'assets/sfx-battle/tether-throw.mp3',
  tetherland:       'assets/sfx-battle/tether-land.mp3',
  meteortelegraph:  'assets/sfx-battle/meteor-telegraph.mp3',
  meteorland:       'assets/sfx-battle/meteor-land.mp3',
  singularityland:  'assets/sfx-battle/singularity-land.mp3',
  ambience:   'assets/sfx-battle/arena-ambience.mp3',
  beam:       'assets/sfx-battle/beam-fire.mp3',
  charge:     'assets/sfx-battle/charge-up.mp3',
  clash:      'assets/sfx-battle/clash-slowmo.mp3',
  gasp:       'assets/sfx-battle/crowd-gasp.mp3',
  dodge:      'assets/sfx-battle/dodge-whoosh.mp3',
  heal:       'assets/sfx-battle/heal-chime.mp3',
  impact:     'assets/sfx-battle/impact-heavy.mp3',
  land:       'assets/sfx-battle/landing-thud.mp3',
  impactbasica: 'assets/sfx-battle/impact-basic-a.mp3',
  impactbasicb: 'assets/sfx-battle/impact-basic-b.mp3',
  impactbasicc: 'assets/sfx-battle/impact-basic-c.mp3',
  impactbasicd: 'assets/sfx-battle/impact-basic-d.mp3',
  impactbasice: 'assets/sfx-battle/impact-basic-e.mp3',
  impactheavya: 'assets/sfx-battle/impact-heavy-a.mp3',
  impactheavyb: 'assets/sfx-battle/impact-heavy-b.mp3',
  impactheavyc: 'assets/sfx-battle/impact-heavy-c.mp3',
  impactheavyd: 'assets/sfx-battle/impact-heavy-d.mp3',
  impactheavye: 'assets/sfx-battle/impact-heavy-e.mp3',
  impactspeciala: 'assets/sfx-battle/impact-special-a.mp3',
  impactspecialb: 'assets/sfx-battle/impact-special-b.mp3',
  impactspecialc: 'assets/sfx-battle/impact-special-c.mp3',
  impactspeciald: 'assets/sfx-battle/impact-special-d.mp3',
  impactspeciale: 'assets/sfx-battle/impact-special-e.mp3',
  impactultimatea: 'assets/sfx-battle/impact-ultimate-a.mp3',
  impactultimateb: 'assets/sfx-battle/impact-ultimate-b.mp3',
  impactultimatec: 'assets/sfx-battle/impact-ultimate-c.mp3',
  ko:         'assets/sfx-battle/ko-shatter.mp3',
  resisted:   'assets/sfx-battle/resisted-thud.mp3',
  shockwave:  'assets/sfx-battle/shockwave.mp3',
  volley:     'assets/sfx-battle/skull-volley.mp3',
  volleya:    'assets/sfx-battle/volley-a.mp3',
  volleyb:    'assets/sfx-battle/volley-b.mp3',
  volleyc:    'assets/sfx-battle/volley-c.mp3',
  supereff:   'assets/sfx-battle/super-effective.mp3',
  trap:       'assets/sfx-battle/trap-spring.mp3',
  ultimate:   'assets/sfx-battle/ultimate-windup.mp3',
  fanfare:    'assets/sfx-battle/victory-fanfare.mp3',
  // SC2-A energy system + per-type attack accents + gastro
  atkiron:    'assets/sfx-battle/atk-iron.mp3',
  atkvoyage:  'assets/sfx-battle/atk-voyage.mp3',
  atkhustle:  'assets/sfx-battle/atk-hustle.mp3',
  atkmystic:  'assets/sfx-battle/atk-mystic.mp3',
  atkchaos:   'assets/sfx-battle/atk-chaos.mp3',
  atkheart:   'assets/sfx-battle/atk-heart.mp3',
  heavyhit:   'assets/sfx-battle/heavy-hit.mp3',
  brace:      'assets/sfx-battle/brace-up.mp3',
  focus:      'assets/sfx-battle/focus-hum.mp3',
  energy:     'assets/sfx-battle/energy-tick.mp3',
  crit:       'assets/sfx-battle/crit-sting.mp3',
  gastro:     'assets/sfx-battle/gastro-rumble.mp3',
  sludge:     'assets/sfx-battle/sludge-impact.mp3',
  // SC12 COMBAT x10: elemental attack identities + physicality + round system
  elemiron:   'assets/sfx-battle/elem-iron.mp3',
  elemvoyage: 'assets/sfx-battle/elem-voyage.mp3',
  elemhustle: 'assets/sfx-battle/elem-hustle.mp3',
  elemmystic: 'assets/sfx-battle/elem-mystic.mp3',
  elemchaos:  'assets/sfx-battle/elem-chaos.mp3',
  elemheart:  'assets/sfx-battle/elem-heart.mp3',
  crashzoom:  'assets/sfx-battle/crash-zoom.mp3',
  hitstop:    'assets/sfx-battle/hit-stop.mp3',
  crunchbasic:   'assets/sfx-battle/crunch-basic.mp3',
  crunchheavy:   'assets/sfx-battle/crunch-heavy.mp3',
  crunchspecial: 'assets/sfx-battle/crunch-special.mp3',
  knockback:  'assets/sfx-battle/knockback.mp3',
  blackout:   'assets/sfx-battle/arena-blackout.mp3',
  ultriser:      'assets/sfx-battle/ult-riser.mp3',
  ultsubdrop:    'assets/sfx-battle/ult-sub-drop.mp3',
  ultshockwave:  'assets/sfx-battle/ult-shockwave.mp3',
  koglass:       'assets/sfx-battle/ko-glass.mp3',
  koboom:        'assets/sfx-battle/ko-low-boom.mp3',
  kotail:        'assets/sfx-battle/ko-reverb-tail.mp3',
  typeclash:  'assets/sfx-battle/type-clash.mp3',
  roundbell:  'assets/sfx-battle/round-bell.mp3',
  // BATTLE EPIC: element × attack-class masters. These are decoded only on
  // battle entry, never in the boot or pack payload.
  epicironbasic:       'assets/sfx-battle/epic-iron-basic.mp3',
  epicironheavy:       'assets/sfx-battle/epic-iron-heavy.mp3',
  epicironspecial:     'assets/sfx-battle/epic-iron-special.mp3',
  epicironultimate:    'assets/sfx-battle/epic-iron-ultimate.mp3',
  epicvoyagebasic:     'assets/sfx-battle/epic-voyage-basic.mp3',
  epicvoyageheavy:     'assets/sfx-battle/epic-voyage-heavy.mp3',
  epicvoyagespecial:   'assets/sfx-battle/epic-voyage-special.mp3',
  epicvoyageultimate:  'assets/sfx-battle/epic-voyage-ultimate.mp3',
  epichustlebasic:     'assets/sfx-battle/epic-hustle-basic.mp3',
  epichustleheavy:     'assets/sfx-battle/epic-hustle-heavy.mp3',
  epichustlespecial:   'assets/sfx-battle/epic-hustle-special.mp3',
  epichustleultimate:  'assets/sfx-battle/epic-hustle-ultimate.mp3',
  epicchaosbasic:      'assets/sfx-battle/epic-chaos-basic.mp3',
  epicchaosheavy:      'assets/sfx-battle/epic-chaos-heavy.mp3',
  epicchaosspecial:    'assets/sfx-battle/epic-chaos-special.mp3',
  epicchaosultimate:   'assets/sfx-battle/epic-chaos-ultimate.mp3',
  epicmysticbasic:     'assets/sfx-battle/epic-mystic-basic.mp3',
  epicmysticheavy:     'assets/sfx-battle/epic-mystic-heavy.mp3',
  epicmysticspecial:   'assets/sfx-battle/epic-mystic-special.mp3',
  epicmysticultimate:  'assets/sfx-battle/epic-mystic-ultimate.mp3',
  epicheartbasic:      'assets/sfx-battle/epic-heart-basic.mp3',
  epicheartheavy:      'assets/sfx-battle/epic-heart-heavy.mp3',
  epicheartspecial:    'assets/sfx-battle/epic-heart-special.mp3',
  epicheartultimate:   'assets/sfx-battle/epic-heart-ultimate.mp3',
  epichitthud:         'assets/sfx-battle/epic-hit-thud.mp3',
  epicmysticcharge:    'assets/sfx-battle/epic-mystic-charge.mp3',
  epicmysticfire:      'assets/sfx-battle/epic-mystic-fire.mp3',
  epicheartswell:      'assets/sfx-battle/epic-heart-swell.mp3',
  epicult:             'assets/sfx-battle/epic-ult.mp3',
  epicko:              'assets/sfx-battle/epic-ko.mp3',
  epicperfect:         'assets/sfx-battle/epic-perfect.mp3',
  // BATTLE DANGER reuses the landing thud at half speed: low, quiet and dry
  // enough to sit under music without adding another binary to the preload.
  dangerbeat: 'assets/sfx-battle/landing-thud.mp3',
};
// Contact variations are complete, measured cues rather than extra layers.
// A shuffle bag exhausts each class before any member can repeat; the refill
// also rejects the previous pick at its boundary so repeats never cluster.
const BATTLE_VARIANTS = {
  basic: ['impactbasica', 'impactbasicb', 'impactbasicc', 'impactbasicd', 'impactbasice'],
  heavy: ['impactheavya', 'impactheavyb', 'impactheavyc', 'impactheavyd', 'impactheavye'],
  special: ['impactspeciala', 'impactspecialb', 'impactspecialc', 'impactspeciald', 'impactspeciale'],
  ultimate: ['impactultimatea', 'impactultimateb', 'impactultimatec'],
  volley: ['volleya', 'volleyb', 'volleyc'],
};
const battleVariantBags = new Map();
const battleVariantLast = new Map();

function battleBagPick(key) {
  const pool = BATTLE_VARIANTS[key];
  if (!pool || !pool.length) return key;
  let bag = battleVariantBags.get(key);
  if (!bag || !bag.length) {
    bag = pool.slice();
    for (let i = bag.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [bag[i], bag[j]] = [bag[j], bag[i]];
    }
    const last = battleVariantLast.get(key);
    if (bag.length > 1 && bag[bag.length - 1] === last) {
      [bag[0], bag[bag.length - 1]] = [bag[bag.length - 1], bag[0]];
    }
    battleVariantBags.set(key, bag);
  }
  const pick = bag.pop();
  battleVariantLast.set(key, pick);
  return pick;
}
const OVERRIDE_KEY = 'nomadmon-sfx-overrides';
export function sfxOverrides() {
  try { return JSON.parse(localStorage.getItem(OVERRIDE_KEY)) || {}; } catch { return {}; }
}
function sfxUrl(name) {
  const vars = SFX_VARIANTS[name];
  const pick = sfxOverrides()[name];
  return vars[(pick && vars[pick]) ? pick : SFX_DEFAULTS[name]];
}
const SFX = Object.fromEntries(Object.keys(SFX_VARIANTS).map(n => [n, () => sfxUrl(n)]));

// P7 music slots — Jack drops real themes into these paths, no code change.
const MUSIC_SLOTS = {
  title:  'assets/music/title-theme.mp3',
  battle: 'assets/music/battle-theme.mp3',
  pack:   'assets/music/pack-theme.mp3',
};
const MUSIC_FALLBACK = 'assets/sfx-src/ambient-loop.mp3';
const MUTE_KEY = 'nomadmon-muted';
const NARR_KEY = 'nomadmon-narrator';

let acx = null, master = null, sfxBus = null, musicBus = null, musicFilter = null, voBus = null;
let musicFilterTarget = 20000;
const buffers = new Map();          // name -> AudioBuffer (sfx + battle sfx)
const musicBuffers = new Map();     // slot -> AudioBuffer
const voBuffers = new Map();        // vo name -> AudioBuffer | null (missing)
let musicSrc = null;
let musicSlotPlaying = null;
let musicBaseGain = 0.42;
let loadPromise = null;
const sfxLoads = new Map();          // name -> in-flight decode promise
const packRequired = new Set();      // exact cues reachable in entered packs
let battleLoadPromise = null;
let unlocked = false;
let battleLoadStartedPerf = null;
let battleLoadReadyPerf = null;
const battleLoads = new Map();
const bufferTransients = new Map();
const battlePlayCounts = new Map();
const TIMED_BATTLE_CUES = new Set(['land', 'impact', 'dodge', 'resisted',
  ...BATTLE_VARIANTS.basic, ...BATTLE_VARIANTS.heavy,
  ...BATTLE_VARIANTS.special, ...BATTLE_VARIANTS.ultimate]);

function ensureGraph() {
  if (acx) return;
  acx = new (window.AudioContext || window.webkitAudioContext)();
  master = acx.createGain();
  master.gain.value = muted() ? 0 : 1;
  // Safety limiter. The god eruption fires six-plus loud layers in the same
  // frame (godsuper + three godaaah voices + crown + spaceshock + shatter);
  // summed on a bare gain node that clips the output hard, which reads as
  // crackle exactly when the ceremony is supposed to peak. This is a ceiling,
  // not a sound: it does nothing until the mix would have clipped anyway.
  const limiter = acx.createDynamicsCompressor();
  limiter.threshold.value = -2; limiter.knee.value = 0;
  limiter.ratio.value = 20; limiter.attack.value = 0.002; limiter.release.value = 0.12;
  master.connect(limiter); limiter.connect(acx.destination);
  sfxBus = acx.createGain(); sfxBus.gain.value = 1; sfxBus.connect(master);
  musicBus = acx.createGain(); musicBus.gain.value = 0;
  // Neutral by default. DANGER owns a slow 900 Hz low-pass without changing
  // music gain/duck ownership, and restoration simply ramps this back open.
  musicFilter = acx.createBiquadFilter();
  musicFilter.type = 'lowpass';
  musicFilter.Q.value = 0.7;
  musicFilter.frequency.value = musicFilterTarget;
  musicBus.connect(musicFilter); musicFilter.connect(master);
  voBus = acx.createGain(); voBus.gain.value = 1; voBus.connect(master);
}

function offlineCapture() {
  return typeof window !== 'undefined' && window.__nomadmonCapture
    && Number.isFinite(window.__nomadmonCapture.audioCaptureOriginMs)
    ? window.__nomadmonCapture : null;
}

function captureAudioEvent(event) {
  const capture = offlineCapture();
  if (!capture) return false;
  const actionSeconds = Math.max(0, (Date.now() - capture.audioCaptureOriginMs) / 1000);
  const events = capture.audioEvents || (capture.audioEvents = []);
  events.push({ captureIndex: events.length,
    actionSeconds: +(actionSeconds + Number(event.when || 0)).toFixed(6), ...event });
  return true;
}

function silentHandle() { return { stop() {}, to() {}, src: null, gainNode: null }; }

async function decode(url) {
  const r = await fetch(`./${url}?${V}`);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return acx.decodeAudioData(await r.arrayBuffer());
}

// A scheduled AudioBufferSource starts at sample zero, which is not
// necessarily where a listener hears the cue. Measure the decoded PCM once,
// during preload, so timing QA can report both the first audible 2ms RMS window
// and the strongest transient without doing waveform work on the hit frame.
function transientTiming(buf) {
  const win = Math.max(1, Math.round(buf.sampleRate * 0.002));
  const floor2 = 10 ** (-24 / 10);
  const sums = new Float64Array(buf.numberOfChannels);
  let onset = null, peak = 0, peakFrame = 0;
  const chans = Array.from({ length: buf.numberOfChannels }, (_, i) => buf.getChannelData(i));
  for (let i = 0; i < buf.length; i++) {
    let framePeak = 0, sum = 0;
    for (let c = 0; c < chans.length; c++) {
      const v = chans[c][i];
      const av = Math.abs(v);
      if (av > framePeak) framePeak = av;
      sums[c] += v * v;
      if (i >= win) {
        const old = chans[c][i - win];
        sums[c] -= old * old;
      }
      sum += sums[c];
    }
    if (framePeak > peak) { peak = framePeak; peakFrame = i; }
    if (onset == null && i >= win - 1 && sum / (win * chans.length) >= floor2) {
      onset = i - win + 1;
    }
  }
  return {
    onsetMs: +(((onset == null ? 0 : onset) / buf.sampleRate) * 1000).toFixed(2),
    peakMs: +((peakFrame / buf.sampleRate) * 1000).toFixed(2),
  };
}

async function loadSfxName(name) {
  if (buffers.has(name)) return buffers.get(name);
  if (sfxLoads.has(name)) return sfxLoads.get(name);
  if (!SFX[name]) throw new Error(`unknown pack sfx: ${name}`);
  const pending = (async () => {
    const buf = await decode(SFX[name]());
    buffers.set(name, buf);
    if (name === 'tradecomplete') bufferTransients.set(name, transientTiming(buf));
    return buf;
  })();
  sfxLoads.set(name, pending);
  try { return await pending; }
  finally { sfxLoads.delete(name); }
}

// Decode a declared pack/UI subset. Each name has one shared in-flight promise,
// so the entry gate and a contents-specific reveal gate can overlap without
// fetching or decoding the same asset twice.
export function loadSfx(names) {
  ensureGraph();
  return Promise.all([...new Set(names)].map(loadSfxName));
}

// These are the only buffers that may fire before pack contents are known:
// the title press, tear interaction, universal card movement/landing, and
// pack controls. Rarity, God and diarrhea suites join through loadPackCues()
// after the draw is known, keeping those large rare-path assets off first paint.
export const PACK_CORE_SFX = Object.freeze([
  'entryhit', 'mpack', 'rip', 'tick', 'shimmer', 'whoosh', 'slam', 'deep',
]);

export function loadPackCues(names = []) {
  const required = [...new Set([...PACK_CORE_SFX, ...names])];
  for (const name of required) packRequired.add(name);
  return loadSfx(required);
}

export function loadPackCore() { return loadPackCues(); }

// Legacy/full-suite callers (audition, trading, deck tools) still get every
// registered buffer. Pack entry deliberately uses the narrower gates above.
export function loadAll() {
  if (loadPromise) return loadPromise;
  loadPromise = loadSfx(Object.keys(SFX)).then(() => {
    console.info(`[nomadmon-audio] full sfx suite ready: ${buffers.size} buffers`);
  });
  return loadPromise;
}

// P7: battle SFX loaded on demand (battle entry), separate from pack sfx
export function loadBattle() {
  if (battleLoadPromise) return battleLoadPromise;
  ensureGraph();
  battleLoadStartedPerf = performance.now();
  battleLoadPromise = (async () => {
    await Promise.all(Object.entries(BATTLE_SFX).map(async ([n, url]) => {
      const started = performance.now();
      try {
        const buf = await decode(url);
        buffers.set('b:' + n, buf);
        if (TIMED_BATTLE_CUES.has(n)) {
          bufferTransients.set('b:' + n, transientTiming(buf));
        }
        battleLoads.set(n, {
          ready: true,
          ms: +(performance.now() - started).toFixed(2),
          readyPerf: +performance.now().toFixed(2),
        });
      }
      catch (e) {
        battleLoads.set(n, { ready: false, ms: +(performance.now() - started).toFixed(2) });
        console.warn(`[nomadmon-audio] battle sfx missing: ${n}`, e);
      }
    }));
    battleLoadReadyPerf = performance.now();
    console.info('[nomadmon-audio] battle sfx ready');
  })();
  return battleLoadPromise;
}

// unlock on first gesture (call from any pointerdown handler)
// flushPending:false (the ENTER GATE / entry ambient) resumes the context but
// leaves a deferred playPool() waiting for the medallion tap's own BANG.
export function unlock({ flushPending = true } = {}) {
  ensureGraph();
  // Return the resume promise so cold-entry flows can prove the graph is
  // running before their first cue. A context can be suspended again by the
  // browser, so every fresh gesture gets a chance to resume it.
  const resumed = acx.state === 'suspended' ? acx.resume() : Promise.resolve();
  unlocked = true;
  // P4: a playPool() that arrived before the first gesture starts now
  if (poolPending && flushPending) { const p = poolPending; poolPending = null; playPool(p.context, p); }
  return resumed;
}

// Timing rows are reconciled into performance.timeOrigin's clock explicitly.
// AudioContext.currentTime is a sample clock and MUST NOT be subtracted from a
// rAF/performance timestamp. getOutputTimestamp() supplies the bridge; the
// fallback samples both clocks together and includes outputLatency.
const sfxLog = [];
const timingPairs = [];
const pendingVisuals = [];
const timedAudio = new Map();
let timingSeq = 0;
let timingFrameNo = 0;
function logSfx(row) {
  sfxLog.push(row);
  if (sfxLog.length > 192) sfxLog.shift();
}
function clockBridge() {
  if (!acx) return null;
  if (typeof acx.getOutputTimestamp === 'function') {
    const ts = acx.getOutputTimestamp();
    if (Number.isFinite(ts.contextTime) && Number.isFinite(ts.performanceTime)
        && ts.performanceTime > 0) {
      return { method: 'getOutputTimestamp', contextTime: ts.contextTime,
        performanceTime: ts.performanceTime };
    }
  }
  return { method: 'sampled-currentTime+outputLatency', contextTime: acx.currentTime,
    performanceTime: performance.now() + (acx.outputLatency || 0) * 1000 };
}
function contextToPerf(contextTime, bridge) {
  return bridge.performanceTime + (contextTime - bridge.contextTime) * 1000;
}
export function sfxDebug() {
  const expectedPack = [...packRequired];
  const loadedPack = expectedPack.filter((name) => buffers.has(name));
  return { log: sfxLog.slice(),
    pairs: timingPairs.slice(),
    packLoad: {
      ready: loadedPack.length === expectedPack.length,
      expected: expectedPack,
      loaded: loadedPack,
      missing: expectedPack.filter((name) => !buffers.has(name)),
      registered: Object.keys(SFX),
    },
    baseLatency: acx ? +((acx.baseLatency || 0) * 1000).toFixed(2) : null,
    outputLatency: acx ? +((acx.outputLatency || 0) * 1000).toFixed(2) : null,
    battleLoad: {
      startedPerf: battleLoadStartedPerf == null ? null : +battleLoadStartedPerf.toFixed(2),
      readyPerf: battleLoadReadyPerf == null ? null : +battleLoadReadyPerf.toFixed(2),
      totalMs: battleLoadStartedPerf == null || battleLoadReadyPerf == null ? null
        : +(battleLoadReadyPerf - battleLoadStartedPerf).toFixed(2),
      ready: battleLoadReadyPerf != null,
      assets: Object.fromEntries(battleLoads),
    },
  };
}

// Called from the state update that creates contact. The mark stays pending
// until timingFrame() runs AFTER composer.render(), so it represents the frame
// that actually contains the new card position/effect, not an update callback
// that merely intended to draw it. The returned id follows the matching cue.
export function timingMark(label, detail = {}) {
  const id = `${label}#${++timingSeq}`;
  const capture = offlineCapture();
  if (capture) {
    const actionSeconds = Math.max(0, (Date.now() - capture.audioCaptureOriginMs) / 1000);
    (capture.visualEvents || (capture.visualEvents = [])).push({
      id, label, actionSeconds: +actionSeconds.toFixed(6), ...detail,
    });
    const marker = document.getElementById('renderSyncMarker');
    if (marker) {
      marker.style.background = '#fff';
      marker.dataset.eventId = id;
      clearTimeout(marker._offTimer);
      marker._offTimer = setTimeout(() => { marker.style.background = '#000'; }, 134);
    }
  }
  pendingVisuals.push({ id, label, markedPerf: performance.now(), detail });
  return id;
}

// Renderer-owned commit point. `rafPerf` is the timestamp supplied to the
// battle render loop; `renderPerf` is sampled just after WebGL submission. The
// signed delta is AUDIO - VISUAL: positive means sound late, negative early.
export function timingFrame(rafPerf) {
  timingFrameNo++;
  if (!pendingVisuals.length) return;
  const renderPerf = performance.now();
  for (const visual of pendingVisuals.splice(0)) {
    const sound = timedAudio.get(visual.id) || null;
    const row = {
      id: visual.id, label: visual.label, frame: timingFrameNo,
      rafPerf: +rafPerf.toFixed(2), renderPerf: +renderPerf.toFixed(2),
      markedPerf: +visual.markedPerf.toFixed(2), ...visual.detail,
      audio: sound ? {
        name: sound.name, contextStart: sound.ctxTime,
        audibleContextStart: sound.audibleCtxTime,
        performanceStart: sound.audioPerf,
        onsetMs: sound.onsetMs, peakMs: sound.peakMs,
        playOrdinal: sound.playOrdinal, bridge: sound.bridge,
        battleReadyBeforeCall: sound.battleReadyBeforeCall,
        msSinceBattleReady: sound.msSinceBattleReady,
      } : null,
      dropped: sound && sound.dropped ? sound.reason : null,
      signedDeltaMs: sound && !sound.dropped
        ? +(sound.audioPerf - rafPerf).toFixed(2) : null,
      signedRenderDeltaMs: sound && !sound.dropped
        ? +(sound.audioPerf - renderPerf).toFixed(2) : null,
      signedPeakDeltaMs: sound && !sound.dropped
        ? +(sound.audioPerf + (sound.peakMs - sound.onsetMs) / sound.rate - rafPerf).toFixed(2)
        : null,
    };
    timingPairs.push(row);
    if (timingPairs.length > 96) timingPairs.shift();
    logSfx({ kind: 'visual-frame', ...row });
    timedAudio.delete(visual.id);
  }
}

export function musicDebug() {
  return {
    slot: musicSlotPlaying,
    base: musicBaseGain,
    gain: musicBus ? musicBus.gain.value : null,
    floor: duckFloor,
  };
}

export function setMusicDangerFilter(active, ramp = 0.6) {
  ensureGraph();
  musicFilterTarget = active ? 900 : Math.min(20000, acx.sampleRate * 0.49);
  const now = acx.currentTime;
  musicFilter.frequency.cancelScheduledValues(now);
  musicFilter.frequency.setValueAtTime(Math.max(20, musicFilter.frequency.value), now);
  musicFilter.frequency.exponentialRampToValueAtTime(musicFilterTarget,
    now + Math.max(0.01, ramp));
}

export function debugMusicFilter() {
  return { cutoff: musicFilter ? +musicFilter.frequency.value.toFixed(1) : musicFilterTarget };
}

export function play(name, { gain = 1, rate = 1, when = 0, timing = '', arm = false, offset = 0 } = {}) {
  const assetUrl = SFX[name] ? SFX[name]() : null;
  if (captureAudioEvent({ kind: 'sfx', bus: 'pack', name, assetUrl, gain, rate, when, timing })) return null;
  const buf = buffers.get(name);
  if (!buf || !acx || (acx.state !== 'running' && !(arm && acx.state === 'suspended'))) {
    const reason = !buf ? 'buffer-not-ready' : !acx ? 'no-context' : `context-${acx.state}`;
    logSfx({ kind: 'drop', bus: 'pack', name, timing,
      perf: +performance.now().toFixed(2), reason });
    if (timing) timedAudio.set(timing, { name, dropped: true,
      reason });
    return null;
  }
  const src = acx.createBufferSource();
  src.buffer = buf; src.playbackRate.value = rate;
  const g = acx.createGain(); g.gain.value = gain;
  src.connect(g); g.connect(sfxBus);
  src.gainNode = g;                        // lets a caller fade a cue it owns (entry skip)
  const at = acx.currentTime + when;
  src.start(at, offset);
  if (timing) {
    const transient = bufferTransients.get(name) || { onsetMs: 0, peakMs: 0 };
    const audibleAt = at + transient.onsetMs / 1000 / rate;
    const bridge = clockBridge();
    timedAudio.set(timing, { name, ctxTime: +at.toFixed(5),
      audibleCtxTime: +audibleAt.toFixed(5), audioPerf: +contextToPerf(audibleAt, bridge).toFixed(2),
      onsetMs: transient.onsetMs, peakMs: transient.peakMs, rate, playOrdinal: 1,
      bridge, battleReadyBeforeCall: true, msSinceBattleReady: null });
  }
  logSfx({ kind: 'sfx', bus: 'pack', name, perf: +performance.now().toFixed(2),
    ctxTime: +at.toFixed(5), now: +acx.currentTime.toFixed(5), gain, rate, timing });
  // Fetch/decode/scheduling are not playback. Keep the semantic row aligned
  // with bplay() so entry and pack QA can prove BufferSource.start() ran.
  logSfx({ kind: 'playing', bus: 'pack', name, perf: +performance.now().toFixed(2),
    ctxTime: +at.toFixed(5), now: +acx.currentTime.toFixed(5), gain, rate, timing });
  return src;
}
// A decoded SFX buffer (or null while it loads), for cues that mix sampled
// layers inside their own graph (levelup-sfx.js routes them through one bus
// with its own ceiling so the whole stack stays <= -1 dBTP).
export function sfxBuffer(name) { return buffers.get(name) || null; }
// v11: a synthesised cue (forge ceremonies). `build(ctx, out, t0)` schedules
// nodes into `out`, which rides sfxBus so mute and the master limiter apply.
export function playSynth(name, build) {
  if (captureAudioEvent({ kind: 'sfx', bus: 'pack', name: `synth:${name}`, assetUrl: null, gain: 1, rate: 1, when: 0, timing: '' })) return null;
  if (!acx || acx.state !== 'running') {
    logSfx({ kind: 'drop', bus: 'pack', name: `synth:${name}`, perf: +performance.now().toFixed(2), reason: !acx ? 'no-context' : `context-${acx.state}` });
    return null;
  }
  const out = acx.createGain();
  out.connect(sfxBus);
  const t0 = acx.currentTime + 0.03;
  const end = build(acx, out, t0);
  logSfx({ kind: 'playing', bus: 'pack', name: `synth:${name}`, perf: +performance.now().toFixed(2), ctxTime: +t0.toFixed(5), now: +acx.currentTime.toFixed(5) });
  setTimeout(() => { try { out.disconnect(); } catch { /* gone */ } }, Math.max(0, (end - acx.currentTime) * 1000 + 500));
  return out;
}
// battle-suite shorthand: bplay('impact', ...) — loop option for ambience
export function bplay(name, {
  gain = 1, rate = 1, when = 0, loop = false, timing = '', event = '',
} = {}) {
  if (captureAudioEvent({ kind: 'sfx', bus: 'battle', name,
    assetUrl: BATTLE_SFX[name] || null, gain, rate, when, loop, timing, event })) return silentHandle();
  const buf = buffers.get('b:' + name);
  const perf = +performance.now().toFixed(2);
  const playOrdinal = (battlePlayCounts.get(name) || 0) + 1;
  battlePlayCounts.set(name, playOrdinal);
  if (!buf || !acx || acx.state !== 'running') {
    const reason = !buf ? 'buffer-not-ready' : !acx ? 'no-context' : `context-${acx.state}`;
    const row = { kind: 'drop', bus: 'battle', name, timing, event, perf, reason, playOrdinal };
    logSfx(row);
    if (timing) timedAudio.set(timing, { ...row, dropped: true });
    return null;
  }
  const src = acx.createBufferSource();
  src.buffer = buf; src.playbackRate.value = rate; src.loop = loop;
  const g = acx.createGain(); g.gain.value = gain;
  src.connect(g); g.connect(sfxBus);
  const at = acx.currentTime + when;
  src.start(at);
  const transient = bufferTransients.get('b:' + name) || { onsetMs: 0, peakMs: 0 };
  const audibleAt = at + transient.onsetMs / 1000 / rate;
  const bridge = clockBridge();
  const audioPerf = contextToPerf(audibleAt, bridge);
  const row = { kind: 'sfx', bus: 'battle', name, timing, event, perf,
    ctxTime: +at.toFixed(5), audibleCtxTime: +audibleAt.toFixed(5),
    now: +acx.currentTime.toFixed(5), gain, rate,
    onsetMs: transient.onsetMs, peakMs: transient.peakMs, playOrdinal,
    bridge, audioPerf: +audioPerf.toFixed(2),
    battleReadyBeforeCall: battleLoadReadyPerf != null && battleLoadReadyPerf <= perf,
    msSinceBattleReady: battleLoadReadyPerf == null ? null : +(perf - battleLoadReadyPerf).toFixed(2) };
  logSfx(row);
  // A separate semantic row means smoke can prove BufferSource.start() was
  // reached. Asset registration, fetch, and decode logs never use `playing`.
  logSfx({ ...row, kind: 'playing' });
  if (timing) timedAudio.set(timing, row);
  let stopped = false;
  return {
    src, gainNode: g,
    stop(fade = 0) {
      if (stopped) return;
      stopped = true;
      const end = acx.currentTime + Math.max(0, fade);
      if (fade > 0) {
        g.gain.cancelScheduledValues(acx.currentTime);
        g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), acx.currentTime);
        g.gain.linearRampToValueAtTime(0.0001, end);
      }
      try { src.stop(end + (fade > 0 ? 0.005 : 0)); } catch { /* already ended */ }
    },
  };
}

export function bplayVariant(key, options = {}) {
  return bplay(battleBagPick(key), options);
}

// SC18 GOD PULL: a looping SFX bed with its own gain envelope. The dread
// heartbeat has to sit UNDER the music for the whole heavy-pack ceremony and
// then swell alone through the stall, so it needs a live handle rather than a
// fire-and-forget play(). Runs through sfxBus (master mute still owns silence).
export function playBed(name, { gain = 0.2, rate = 1, fadeIn = 0.6, loopStart = 0, loopEnd = 0 } = {}) {
  const buf = buffers.get(name);
  if (!buf || !acx || acx.state !== 'running') {
    logSfx({ kind: 'drop', bus: 'pack-bed', name, perf: +performance.now().toFixed(2),
      reason: !buf ? 'buffer-not-ready' : !acx ? 'no-context' : `context-${acx.state}` });
    return null;
  }
  const src = acx.createBufferSource();
  src.buffer = buf; src.loop = true; src.playbackRate.value = rate;
  if (loopEnd > loopStart) { src.loopStart = loopStart; src.loopEnd = loopEnd; }
  const g = acx.createGain();
  g.gain.setValueAtTime(0.0001, acx.currentTime);
  g.gain.linearRampToValueAtTime(gain, acx.currentTime + Math.max(fadeIn, 0.01));
  src.connect(g); g.connect(sfxBus);
  src.start(0, loopStart);
  logSfx({ kind: 'sfx', bus: 'pack-bed', name, perf: +performance.now().toFixed(2),
    ctxTime: +acx.currentTime.toFixed(5), now: +acx.currentTime.toFixed(5), gain, rate });
  let dead = false;
  return {
    to(level, t = 0.4) {
      if (dead) return;
      g.gain.cancelScheduledValues(acx.currentTime);
      g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), acx.currentTime);
      g.gain.linearRampToValueAtTime(Math.max(level, 0.0001), acx.currentTime + t);
    },
    stop(fade = 0.5) {
      if (dead) return;
      dead = true;
      g.gain.cancelScheduledValues(acx.currentTime);
      g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), acx.currentTime);
      g.gain.linearRampToValueAtTime(0.0001, acx.currentTime + fade);
      try { src.stop(acx.currentTime + fade + 0.05); } catch { /* already stopped */ }
      setTimeout(() => { try { g.disconnect(); } catch { /* gone */ } }, fade * 1000 + 120);
    },
  };
}

// ---------------------------------------------------------------- whisper bus
// SC2-B P6 (c): card-focus clip audio. Each shared living <video> element gets
// its own gain node so an ordinary living card can remain a 5% whisper while a
// diarrhea reveal can be full-volume without also blasting the enemy card in
// battle. MediaElementSource can only be created once per element, hence the
// WeakMap. Every node still terminates at master, so mute remains absolute.
const whisperNodes = new WeakMap();
export function attachWhisper(el, gain = 0.05) {
  if (!acx || acx.state !== 'running') return false;   // needs the unlock gesture
  let node = whisperNodes.get(el);
  if (!node) {
    try {
      node = acx.createGain();
      node.gain.value = Math.max(0, Math.min(gain, 1));
      acx.createMediaElementSource(el).connect(node);
      node.connect(master);
      whisperNodes.set(el, node);
    }
    catch { return false; }
  }
  const target = Math.max(0, Math.min(gain, 1));
  if (Math.abs(node.gain.value - target) > 0.001) {
    node.gain.cancelScheduledValues(acx.currentTime);
    node.gain.setValueAtTime(target, acx.currentTime);
  }
  return true;
}

// ---------------------------------------------------------------- heartbeat
// SC2-B P6 (d): synthesized pre-reveal heartbeat for legendary+ telegraphs —
// low sine lub-dub thumps (no binary asset), accelerating + swelling over the
// build window until the flip. Scheduled ahead on the WebAudio clock; runs
// through sfxBus so master mute silences it.
let hbGainNode = null, hbTimer = 0, hbNextT = 0, hbStart = 0, hbBuild = 1.8;
export function startHeartbeat({ gain = 0.6, build = 1.8 } = {}) {
  ensureGraph();
  if (acx.state !== 'running') return;
  stopHeartbeat(0.02);
  hbGainNode = acx.createGain();
  hbGainNode.gain.value = gain;
  hbGainNode.connect(sfxBus);
  hbStart = acx.currentTime; hbNextT = hbStart + 0.06; hbBuild = Math.max(build, 0.4);
  const bus = hbGainNode;
  const thump = (t, f, g) => {
    const o = acx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f, t);
    o.frequency.exponentialRampToValueAtTime(f * 0.62, t + 0.16);
    const e = acx.createGain();
    e.gain.setValueAtTime(0.0001, t);
    e.gain.exponentialRampToValueAtTime(g, t + 0.014);
    e.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(e); e.connect(bus);
    o.start(t); o.stop(t + 0.26);
  };
  const schedule = () => {
    if (hbGainNode !== bus) return;
    while (hbNextT < acx.currentTime + 0.45) {
      const k = Math.min((hbNextT - hbStart) / hbBuild, 1);   // 0 calm .. 1 frantic
      thump(hbNextT, 52, 0.4 + 0.6 * k);                      // lub
      thump(hbNextT + 0.15, 43, (0.4 + 0.6 * k) * 0.7);       // dub
      hbNextT += 0.95 - 0.48 * k;
    }
  };
  schedule();
  hbTimer = setInterval(schedule, 120);
}
export function stopHeartbeat(fade = 0.18) {
  if (hbTimer) { clearInterval(hbTimer); hbTimer = 0; }
  if (!hbGainNode) return;
  const g = hbGainNode;
  hbGainNode = null;
  g.gain.cancelScheduledValues(acx.currentTime);
  g.gain.setValueAtTime(g.gain.value, acx.currentTime);
  g.gain.linearRampToValueAtTime(0, acx.currentTime + fade);
  setTimeout(() => { try { g.disconnect(); } catch { /* already gone */ } }, fade * 1000 + 80);
}

// ---------------------------------------------------------------- music slots
async function musicBuffer(slot) {
  if (musicBuffers.has(slot)) return musicBuffers.get(slot);
  let buf = null;
  try { buf = await decode(MUSIC_SLOTS[slot]); }
  catch {
    try { buf = await decode(MUSIC_FALLBACK); }
    catch { buf = null; }
  }
  musicBuffers.set(slot, buf);
  return buf;
}

// crossfades from whatever is playing to the named slot's theme
export async function playMusic(slot, { gain = 0.42, fade = 1.6 } = {}) {
  ensureGraph();
  if (musicSlotPlaying === slot && musicSrc) return;
  // v117: kill the stream pool NOW (before the decode await) so the menu
  // theme can never ride on top of the pack theme (Jack, 2026-09-06).
  stopMusic({ fade: Math.min(fade, 0.8) });
  const buf = await musicBuffer(slot);
  if (!buf) return;
  if (musicSlotPlaying === slot && musicSrc) return;   // re-check after await
  stopMusic({ fade: Math.min(fade, 0.8) });
  const src = acx.createBufferSource();
  src.buffer = buf; src.loop = true;
  src.connect(musicBus);
  musicSrc = src; musicSlotPlaying = slot; musicBaseGain = gain;
  musicBus.gain.cancelScheduledValues(acx.currentTime);
  musicBus.gain.setValueAtTime(musicBus.gain.value, acx.currentTime);
  musicBus.gain.linearRampToValueAtTime(gain, acx.currentTime + fade);
  src.start();
}

// legacy API (pack.js): startMusic == playMusic('pack')
export function startMusic(opts = {}) { playMusic('pack', opts); }

function duckSlot(level, t) {
  if (!acx || !musicSrc) return;
  musicBus.gain.cancelScheduledValues(acx.currentTime);
  musicBus.gain.setValueAtTime(musicBus.gain.value, acx.currentTime);
  musicBus.gain.linearRampToValueAtTime(level, acx.currentTime + t);
}
// duckFloor: while set, every duck/unduck clamps to it — the gastro ceremony
// holds music at SILENCE even if another vo line's onended tries to unduck.
let duckFloor = null;
function applyDuck(slotLevel, poolFactor, t) {
  if (duckFloor != null) {
    slotLevel = Math.min(slotLevel, duckFloor);
    poolFactor = Math.min(poolFactor, duckFloor / Math.max(poolBase, 0.001));
  }
  poolDuckTo(Math.max(0, Math.min(poolFactor, 1)), t);
  duckSlot(slotLevel, t);
}
export function duckMusic(level, t = 0.4) {
  applyDuck(level, level / Math.max(poolBase, 0.001), t);
}
export function unduckMusic(t = 0.8) {
  applyDuck(musicBaseGain, 1, t);
}
let impactDuckTimer = 0;
export function duckMusicForImpact(db = -6, holdMs = 300) {
  const factor = 10 ** (Math.min(0, db) / 20);
  applyDuck(musicBaseGain * factor, factor, 0.025);
  clearTimeout(impactDuckTimer);
  impactDuckTimer = setTimeout(() => {
    impactDuckTimer = 0;
    unduckMusic(0.12);
  }, Math.max(0, holdMs));
}
export function holdMusicDuck(level = 0, t = 0.25) {
  duckFloor = Math.max(0, level);
  applyDuck(duckFloor, duckFloor / Math.max(poolBase, 0.001), t);
}
export function holdMusicSilent(t = 0.25) { holdMusicDuck(0, t); }
export function releaseMusicSilence(t = 1.4) { duckFloor = null; unduckMusic(t); }

export function stopMusic({ fade = 1.0, pool = true } = {}) {
  duckFloor = null;                               // full stop resets duck state
  clearTimeout(impactDuckTimer); impactDuckTimer = 0;
  if (pool) { stopPool(fade); poolDuckTo(1, fade); }
  if (!acx || !musicSrc) return;
  const src = musicSrc; musicSrc = null; musicSlotPlaying = null;
  musicBus.gain.cancelScheduledValues(acx.currentTime);
  musicBus.gain.setValueAtTime(musicBus.gain.value, acx.currentTime);
  musicBus.gain.linearRampToValueAtTime(0, acx.currentTime + fade);
  src.stop(acx.currentTime + fade + 0.05);
}

// ---------------------------------------------------------------- stream music pool
// P4 (SC2-B): manifest-driven music (assets/music/manifest.json). The DO Spaces
// CDN sends no Access-Control-Allow-Origin, so pool tracks stream through TWO
// plain <audio> elements (no decodeAudioData) crossfaded by a volume ticker.
// The WebAudio slot system above stays on for the pack ceremony theme.
// duckMusic/unduckMusic/stopMusic/setMuted all drive these elements too.
const POOL_MANIFEST_URL = 'assets/music/manifest.json';
const POOL_BASE = { title: 0.65, battle: 0.6, pack: 0.55 };  // Jack: title LOUD
let poolManifest = null, poolManifestP = null;
const poolEls = [null, null];       // two lazily-created <audio> elements
let poolActive = -1;                // element index currently fronting
let poolContext = null;             // context playing (null = stopped)
let poolBase = 0.6;
let poolDuck = 1, poolDuckT = 1, poolDuckTau = 0.15;   // duck factor + target
let poolPending = null;             // play intent deferred until unlock()
let poolTicker = 0;
const poolLast = {};                // context -> last track name (no repeats)
let poolLastGlobal = null;          // also never repeat the previous track
let poolNext = null;                // planned next entry (preloaded on idle el)
let toastT = 0;

function poolSrc(entry) {
  return /^https?:/.test(entry.url) ? entry.url : `./${entry.url}?${V}`;
}
function poolEl(i) {
  if (!poolEls[i]) {
    const el = new Audio();
    el.preload = 'auto';
    el.muted = muted();
    el.volume = 0;
    el._env = 0; el._envT = 0; el._rate = 1;   // fade envelope 0..1 + target
    el.addEventListener('ended', () => {
      if (poolActive === i && poolContext) poolAdvance();
    });
    poolEls[i] = el;
  }
  return poolEls[i];
}
function poolPick(context, selection = null) {
  const all = (poolManifest || []).filter(e => e.context === context);
  if (!all.length) return null;
  if (context === 'battle' && selection && Number.isInteger(Number(selection.seed))) {
    return selectBattleMusic(poolManifest, Number(selection.seed), selection.arenaKey);
  }
  let c = all.filter(e => e.name !== poolLast[context] && e.name !== poolLastGlobal);
  if (!c.length) c = all;
  return c[Math.floor(Math.random() * c.length)];
}
function poolManifestLoad() {
  poolManifestP = poolManifestP ||
    fetch(`./${POOL_MANIFEST_URL}?${V}`).then(r => r.json())
      .then(m => { poolManifest = m; })
      .catch((e) => { console.warn('[nomadmon-audio] music manifest missing', e); poolManifest = []; });
  return poolManifestP;
}
function poolDuckTo(f, t) {
  poolDuckT = f;
  poolDuckTau = Math.max(t, 0.05) / 3;
  poolRun();
}
// one 50ms ticker owns every element volume: env crossfades × duck × base
function poolRun() {
  if (poolTicker) return;
  poolTicker = setInterval(() => {
    const dt = 0.05;
    poolDuck += (poolDuckT - poolDuck) * (1 - Math.exp(-dt / poolDuckTau));
    if (Math.abs(poolDuck - poolDuckT) < 0.002) poolDuck = poolDuckT;
    let live = Math.abs(poolDuck - poolDuckT) > 0.001 || !!poolContext;
    for (const el of poolEls) {
      if (!el) continue;
      const d = el._envT - el._env;
      const step = el._rate * dt;
      el._env = Math.abs(d) <= step ? el._envT : el._env + Math.sign(d) * step;
      el.volume = Math.max(0, Math.min(1, el._env * poolBase * poolDuck));
      if (el._envT === 0 && el._env === 0 && !el.paused) el.pause();
      if (el._env !== el._envT || el._env > 0) live = true;
    }
    if (!live) { clearInterval(poolTicker); poolTicker = 0; }
  }, 50);
}
function poolStart(entry, fade) {
  if (!entry) return;
  primed = null;                       // SC6: any normal start voids the prime
  const inIdx = poolActive === 0 ? 1 : 0;
  const inc = poolEl(inIdx);
  const out = poolActive >= 0 ? poolEls[poolActive] : null;
  inc.src = poolSrc(entry);
  inc.currentTime = 0;
  inc._env = 0; inc._envT = 1; inc._rate = 1 / Math.max(fade, 0.05);
  inc.volume = 0;
  inc.play().catch(() => { /* autoplay raced — next play call restarts */ });
  if (out && out !== inc) { out._envT = 0; out._rate = 1 / Math.max(fade, 0.05); }
  poolActive = inIdx;
  poolLast[poolContext] = entry.name;
  poolLastGlobal = entry.name;
  poolNext = poolPick(poolContext);        // plan (and later preload) the next
  poolRun();
  poolToast(entry.name);
  // preload the NEXT track on the idle element once its fade-out settles
  setTimeout(() => {
    const idle = poolEls[1 - poolActive];
    if (idle && poolNext && idle._env === 0 && poolContext) idle.src = poolSrc(poolNext);
  }, (fade + 0.4) * 1000);
}
function poolAdvance() {
  const entry = (poolNext && poolNext.context === poolContext)
    ? poolNext : poolPick(poolContext);
  poolNext = null;
  poolStart(entry, 0.8);
}
function stopPool(fade) {
  poolContext = null;
  poolPending = null;
  poolNext = null;
  primed = null;
  for (const el of poolEls) {
    if (el) { el._envT = 0; el._rate = 1 / Math.max(fade, 0.05); }
  }
  poolRun();
}
// ---------------------------------------------------------------- SC6 prime + BANG
// primePool(context): called during the ENTRY SCREEN, before the unlock
// gesture. Loads the manifest, picks the session's track for `context`, and
// warms it MUTED-PLAYING (browsers allow muted autoplay) so the first
// playPool(context) after unlock() slams to full volume with ~0 latency —
// the app opens with a bang. If even muted playback is blocked the element
// still preloads, and the bang path cold-plays the buffered data instead.
let primed = null;   // { context, entry, ok } — ok: muted playback is rolling
export async function primePool(context) {
  await poolManifestLoad();
  if (primed || poolContext) return;        // something already started
  const entry = poolPick(context);
  if (!entry) return;
  const el = poolEl(0);
  el.src = poolSrc(entry);
  el.muted = true;                          // priming is ALWAYS silent
  el._env = 0; el._envT = 0;
  el.volume = 0;
  primed = { context, entry, ok: false };
  try {
    await el.play();
    if (primed && primed.entry === entry) primed.ok = true;
    console.info(`[nomadmon-audio] primed muted-playing: ${entry.name}`);
  } catch {
    console.info(`[nomadmon-audio] primed preload-only (muted autoplay blocked): ${entry.name}`);
  }
}

// the BANG: primed element goes 0 -> full context volume instantly (no fade).
// Latency from the call (the unlock tap) to first audible sample is logged.
function bangPrimed(context, gain) {
  const p = primed; primed = null;
  const el = poolEls[0];
  if (!el || el.error || !p.entry) return false;
  const t0 = performance.now();
  poolContext = context;
  poolBase = gain ?? POOL_BASE[context] ?? 0.6;
  poolActive = 0;
  poolDuck = 1; poolDuckT = 1;
  el._env = 1; el._envT = 1; el._rate = 20;
  el.muted = muted();
  el.volume = Math.max(0, Math.min(1, poolBase));
  const report = (how) => console.info(
    `[nomadmon-audio] BANG ${context} "${p.entry.name}": audible in ` +
    `${(performance.now() - t0).toFixed(1)}ms (${how})`);
  if (p.ok && !el.paused) {
    // already muted-playing: audible the instant the unmute lands; restart
    // from the top — the seek stays inside buffered data
    el.addEventListener('seeked', () => report('primed muted-playing, seek-to-0'), { once: true });
    try { el.currentTime = 0; } catch { report('primed muted-playing, no-seek'); }
  } else {
    el.addEventListener('playing', () => report('primed preload, cold play'), { once: true });
    try { el.currentTime = 0; } catch { /* not seekable yet */ }
    el.play().catch(() => { /* hard-blocked — next playPool call restarts */ });
  }
  poolLast[context] = p.entry.name;
  poolLastGlobal = p.entry.name;
  poolNext = poolPick(context);
  poolRun();
  poolToast(p.entry.name);
  setTimeout(() => {                        // preload the NEXT track (idle el)
    const idle = poolEls[1];
    if (idle && poolNext && idle._env === 0 && poolContext) idle.src = poolSrc(poolNext);
  }, 1400);
  return true;
}

// picks a random manifest track of `context`, crossfades from whatever plays.
// Calling with the context already fronting is a no-op (screens re-enter).
export async function playPool(context, { gain, fade = 1.6, selection = null } = {}) {
  if (offlineCapture()) {
    if (!poolManifest) await poolManifestLoad();
    const entry = poolPick(context, selection);
    if (entry) captureAudioEvent({ kind: 'music', bus: 'music-pool', name: entry.name,
      assetUrl: entry.url, gain: gain ?? POOL_BASE[context] ?? 0.6,
      rate: 1, when: 0, loop: true, selection: selection || null });
    return;
  }
  if (!unlocked) { poolPending = { context, gain, fade }; return; }
  if (poolContext === context) return;
  // SC6: the first play of a primed context detonates instead of fading in
  if (primed && primed.context === context && bangPrimed(context, gain)) return;
  poolContext = context;
  poolBase = gain ?? POOL_BASE[context] ?? 0.6;
  if (!poolManifest) await poolManifestLoad();
  if (poolContext !== context) return;      // superseded during the await
  poolStart(poolPick(context, selection), fade);
}
// now-playing toast: fades in on track start, auto-fades after ~3.5s
function poolToast(name) {
  const el = document.getElementById('npToast');
  if (!el) return;
  el.textContent = `♪ ${name}`;
  el.classList.add('show');
  clearTimeout(toastT);
  toastT = setTimeout(() => el.classList.remove('show'), 3500);
}

// ---------------------------------------------------------------- narrator VO
// One channel, never overlaps itself: a new line either interrupts (priority)
// or is dropped while one is speaking. Music ducks under every line. Missing
// files fail silent (Phase 3 ships them). Skippable: voStop().
let voPlaying = null;    // { src, name }
export function narratorOn() {
  try { return localStorage.getItem(NARR_KEY) !== '0'; } catch { return true; }
}
export function setNarrator(on) {
  try { localStorage.setItem(NARR_KEY, on ? '1' : '0'); } catch { /* private mode */ }
  if (!on) voStop();
}
async function voBuffer(name) {
  if (voBuffers.has(name)) return voBuffers.get(name);
  let buf = null;
  try { ensureGraph(); buf = await decode(`assets/vo/${name}.mp3`); } catch { buf = null; }
  voBuffers.set(name, buf);
  return buf;
}
export function voPreload(names) { for (const n of names) voBuffer(n); }
export async function vo(name, { gain = 1.0, interrupt = false, duck = 0.12 } = {}) {
  if (!narratorOn()) return false;
  if (captureAudioEvent({ kind: 'vo', bus: 'vo', name,
    assetUrl: `assets/vo/${name}.mp3`, gain, rate: 1, when: 0, interrupt, duck })) return true;
  ensureGraph();
  if (acx.state !== 'running') return false;
  if (voPlaying && !interrupt) return false;           // never overlaps itself
  const buf = await voBuffer(name);
  if (!buf) return false;
  if (voPlaying && !interrupt) return false;           // channel changed during decode
  if (voPlaying) { try { voPlaying.src.stop(); } catch { /* raced */ } voPlaying = null; }
  const src = acx.createBufferSource();
  src.buffer = buf;
  const g = acx.createGain(); g.gain.value = gain;
  src.connect(g); g.connect(voBus);
  duckMusic(Math.min(duck, musicBaseGain), 0.25);
  const me = { src, name };
  voPlaying = me;
  return new Promise((resolve) => {
    src.onended = () => {
      if (voPlaying === me) { voPlaying = null; unduckMusic(0.9); }
      resolve(true);
    };
    src.start();
    console.info(`[nomadmon-audio] vo: ${name}`);   // SC14: trigger log (QA matrix)
  });
}
// P5: play an arbitrary same-origin VO file through the vo channel — identical
// no-overlap + duck semantics to vo(). Buffers cache by path; commentary.js
// feeds Shao's battle lines (assets/vo-battle/*) through here. holdDuck hands
// music-duck ownership to the caller (the gastro ceremony owns its silence).
export async function voFile(path, { gain = 1.0, interrupt = false, duck = 0.12, holdDuck = false } = {}) {
  if (!narratorOn()) return;
  if (captureAudioEvent({ kind: 'vo', bus: 'vo', name: path, assetUrl: path,
    gain, rate: 1, when: 0, interrupt, duck, holdDuck })) return;
  ensureGraph();
  if (acx.state !== 'running') return;
  if (voPlaying && !interrupt) return;               // never overlaps itself
  if (!voBuffers.has(path)) {
    let b = null;
    try { b = await decode(path); } catch { b = null; }
    voBuffers.set(path, b);
  }
  const buf = voBuffers.get(path);
  if (!buf) return;
  if (voPlaying && !interrupt) return;               // re-check after await
  if (voPlaying) { try { voPlaying.src.stop(); } catch { /* raced */ } voPlaying = null; }
  const src = acx.createBufferSource();
  src.buffer = buf;
  const g = acx.createGain(); g.gain.value = gain;
  src.connect(g); g.connect(voBus);
  if (!holdDuck) duckMusic(Math.min(duck, musicBaseGain), 0.25);
  const me = { src, name: path };
  voPlaying = me;
  src.onended = () => {
    if (voPlaying === me) { voPlaying = null; if (!holdDuck) unduckMusic(0.9); }
  };
  src.start();
}

// SC9-E: Shao screen narrations — once per screen per SESSION. Fires only
// when the channel is free (busy = silent skip, retries on the next entry,
// which also covers the pre-unlock boot path). Ducks music via vo(); narrator
// toggle respected. Names map to assets/vo/screen-<name>.mp3.
const SCREEN_VO_KEY = 'nomadmon-screenvo';
// SC14: revisitName — a second-visit alternate line. First entry keeps the
// original narration; later entries occasionally (~40%) get the revisit line,
// itself once per session, so screens never go fully mute on return.
export function screenVo(name, revisitName = null) {
  if (!narratorOn()) return false;
  let seen = {};
  try { seen = JSON.parse(sessionStorage.getItem(SCREEN_VO_KEY)) || {}; } catch { /* fresh */ }
  if (seen[name]) {
    if (!revisitName || seen[revisitName] || voPlaying || Math.random() > 0.4) return false;
    if (!acx || acx.state !== 'running') return false;
    seen[revisitName] = 1;
    try { sessionStorage.setItem(SCREEN_VO_KEY, JSON.stringify(seen)); } catch { /* private */ }
    vo(revisitName);
    console.info(`[nomadmon-audio] screen vo revisit: ${revisitName}`);
    return true;
  }
  if (voPlaying) return false;                       // busy: silent skip
  if (!acx || acx.state !== 'running') return false; // pre-unlock: retry later
  seen[name] = 1;
  try { sessionStorage.setItem(SCREEN_VO_KEY, JSON.stringify(seen)); } catch { /* private */ }
  vo(`screen-${name}`);
  console.info(`[nomadmon-audio] screen vo: ${name}`);
  return true;
}

export function voStop() {
  if (!voPlaying) return;
  try { voPlaying.src.stop(); } catch { /* already done */ }
  voPlaying = null;
  unduckMusic(0.5);
}
export function voActive() { return !!voPlaying; }

// SC6 QA: live pool state (remote QA proves PLAYBACK, not just fetch)
export function poolDebug() {
  return {
    context: poolContext, active: poolActive, base: poolBase, duck: poolDuck,
    els: poolEls.map(el => el && {
      src: (el.src || '').split('/').pop().split('?')[0], paused: el.paused,
      muted: el.muted, volume: el.volume, time: el.currentTime, env: el._env,
    }),
  };
}

// Mute is a SESSION preference, not a durable one: the sound is most of what
// makes an opening feel like an event, and a mute tapped once (in a meeting, on
// a bus) should not silence the game forever. sessionStorage holds it for the
// life of the tab and drops it on the next visit, so every session starts loud.
export function muted() {
  try { return sessionStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}
export function setMuted(m) {
  try { sessionStorage.setItem(MUTE_KEY, m ? '1' : '0'); } catch { /* private mode */ }
  for (const el of poolEls) if (el) el.muted = m;      // P4: pool obeys mute
  if (master) {
    master.gain.cancelScheduledValues(acx.currentTime);
    master.gain.setValueAtTime(master.gain.value, acx.currentTime);
    master.gain.linearRampToValueAtTime(m ? 0 : 1, acx.currentTime + 0.15);
  }
}

// v149 HOLO PULL: the hologram figure rising out of a pack card. Synthesised
// (no asset): a soft sub boom + noise puff at the light burst, a rising
// magical tone (two detuned voices sweeping up an octave and a fifth through
// an opening low-pass), and a layered shimmer of high bell partials that
// sparkle up the rise. Calm (reduced motion / layered under the god ceremony):
// no boom, quieter. Sum of layer peaks <= 0.42 linear (-7.5 dBFS) before the
// master limiter, so the true peak stays well under -1 dBTP. Everything ends
// at sfxBus -> master, so mute is absolute. Deterministic partials, no rng.
export function playHoloPull({ calm = false, gain = 1 } = {}) {
  ensureGraph();
  if (!acx || acx.state !== 'running') return { stop() {} };
  const t0 = acx.currentTime + 0.01;
  const out = acx.createGain(); out.gain.value = gain; out.connect(sfxBus);
  const env = (node, at, peak, a, d) => {
    node.gain.setValueAtTime(0.0001, at);
    node.gain.exponentialRampToValueAtTime(peak, at + a);
    node.gain.exponentialRampToValueAtTime(0.0001, at + a + d);
  };
  const stops = [];
  if (!calm) {
    // soft boom: sine drop 82 -> 36 Hz + a low-passed noise puff
    const o = acx.createOscillator(); o.type = 'sine';
    o.frequency.setValueAtTime(82, t0); o.frequency.exponentialRampToValueAtTime(36, t0 + 0.7);
    const g = acx.createGain(); env(g, t0, 0.16, 0.012, 0.9);
    o.connect(g); g.connect(out); o.start(t0); o.stop(t0 + 1.0); stops.push(o);
    const n = acx.createBufferSource();
    const nb = acx.createBuffer(1, Math.floor(acx.sampleRate * 0.5), acx.sampleRate);
    const ch = nb.getChannelData(0);
    let s = 12345;                                   // deterministic LCG noise
    for (let i = 0; i < ch.length; i++) { s = (s * 1103515245 + 12345) & 0x7fffffff; ch[i] = (s / 0x3fffffff - 1) * (1 - i / ch.length); }
    n.buffer = nb;
    const lp = acx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 420;
    const ng = acx.createGain(); env(ng, t0, 0.06, 0.01, 0.45);
    n.connect(lp); lp.connect(ng); ng.connect(out); n.start(t0); stops.push(n);
  }
  // rising magical tone
  const dur = 2.1;
  const lp = acx.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 3;
  lp.frequency.setValueAtTime(500, t0); lp.frequency.exponentialRampToValueAtTime(4200, t0 + dur);
  const tg = acx.createGain();
  tg.gain.setValueAtTime(0.0001, t0);
  tg.gain.exponentialRampToValueAtTime(calm ? 0.035 : 0.06, t0 + dur * 0.7);
  tg.gain.exponentialRampToValueAtTime(0.0001, t0 + dur + 0.9);
  lp.connect(tg); tg.connect(out);
  for (const [type, det] of [['triangle', -7], ['sawtooth', 7]]) {
    const o = acx.createOscillator(); o.type = type; o.detune.value = det;
    o.frequency.setValueAtTime(196, t0);
    o.frequency.exponentialRampToValueAtTime(587, t0 + dur);
    const g = acx.createGain(); g.gain.value = type === 'sawtooth' ? 0.35 : 1;
    o.connect(g); g.connect(lp); o.start(t0); o.stop(t0 + dur + 1); stops.push(o);
  }
  // shimmer: bell partials sparkling up the rise
  const bells = [1568, 2093, 2349, 2637, 3136, 3520, 4186, 4699, 5274];
  for (let i = 0; i < bells.length; i++) {
    const at = t0 + 0.15 + i * 0.21;
    const o = acx.createOscillator(); o.type = 'sine'; o.frequency.value = bells[i];
    const trem = acx.createOscillator(); trem.frequency.value = 7 + (i % 3);
    const pk = (calm ? 0.012 : 0.02) * (1 - i * 0.05);
    const tgain = acx.createGain(); tgain.gain.value = pk * 0.3;     // tremolo depth, relative
    const g = acx.createGain(); env(g, at, pk, 0.02, 1.1);
    trem.connect(tgain); tgain.connect(g.gain);
    o.connect(g); g.connect(out);
    o.start(at); o.stop(at + 1.3); trem.start(at); trem.stop(at + 1.3); stops.push(o, trem);
  }
  logSfx({ kind: 'sfx', bus: 'pack', name: 'holopull', perf: +performance.now().toFixed(2), ctxTime: +t0.toFixed(5), gain, calm });
  return {
    stop() {
      try {
        const now = acx.currentTime;
        out.gain.cancelScheduledValues(now); out.gain.setValueAtTime(out.gain.value, now);
        out.gain.linearRampToValueAtTime(0, now + 0.04);
        for (const s of stops) { try { s.stop(now + 0.05); } catch { /* already stopped */ } }
      } catch { /* gone */ }
    },
  };
}
